import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiDelete, apiGet, apiPost, apiPut } from "../../shared/apiClient";
import { useToast } from "../../shared/ToastContext";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { getApiBaseUrl } from "../../config/appSettings";

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

const initialForm = {
  title: "",
  description: "",
  image: "",
  order: 0
};

function resolveMediaUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  const base = getApiBaseUrl();
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${path}`;
}

function getRowValue(row, key) {
  if (!row || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  return row[key] ?? row[pascalKey];
}

function filterGalleryRows(rows, search) {
  const term = search.trim().toLowerCase();
  if (!term) return rows;
  return rows.filter((row) => {
    const title = String(getRowValue(row, "title") || "").toLowerCase();
    const description = String(getRowValue(row, "description") || "").toLowerCase();
    return title.includes(term) || description.includes(term);
  });
}

export function GalleriesPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(0);
  const [editing, setEditing] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState(initialForm);

  const effectiveCustomerId = isAdmin
    ? selectedCustomerId
      ? Number(selectedCustomerId)
      : null
    : Number(user?.customerId || 0) || null;

  const readyToLoad = !isAdmin || selectedCustomerId !== "";

  const resolveCustomerCode = useCallback(() => {
    if (isAdmin) {
      if (!effectiveCustomerId) return "";
      const customer = customers.find(
        (c) => Number(getRowValue(c, "id")) === effectiveCustomerId
      );
      return String(getRowValue(customer, "code") || "").trim();
    }
    return String(user?.customerCode || "").trim();
  }, [isAdmin, effectiveCustomerId, customers, user?.customerCode]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    void (async () => {
      const res = await apiGet("/api/Customers");
      if (cancelled || !res.ok) return;
      const list = Array.isArray(res.data) ? res.data : [];
      setCustomers(list);
      setSelectedCustomerId((prev) => {
        if (prev) return prev;
        const firstId = getRowValue(list[0], "id");
        return firstId ? String(firstId) : "";
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);

    const customerCode = resolveCustomerCode();
    if (customerCode) {
      const pubRes = await apiGet(
        `/api/Galleries/public/customer/${encodeURIComponent(customerCode)}`
      );
      if (pubRes.ok && Array.isArray(pubRes.data)) {
        setRows(filterGalleryRows(pubRes.data, search));
        setLoading(false);
        return;
      }
    }

    const query = new URLSearchParams();
    if (search.trim()) query.set("search", search.trim());
    if (effectiveCustomerId) query.set("customerId", String(effectiveCustomerId));
    const path = query.toString() ? `/api/Galleries?${query}` : "/api/Galleries";
    const res = await apiGet(path);
    setLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to load galleries"), "error");
      return;
    }
    setRows(Array.isArray(res.data) ? res.data : []);
  }, [effectiveCustomerId, resolveCustomerCode, search, toast]);

  useEffect(() => {
    if (!readyToLoad) return;
    void load();
  }, [load, readyToLoad]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...initialForm,
      customerId: isAdmin ? selectedCustomerId : String(user?.customerId ?? "")
    });
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      customerId: String(getRowValue(row, "customerId") ?? user?.customerId ?? ""),
      title: getRowValue(row, "title") ?? "",
      description: getRowValue(row, "description") ?? "",
      image: getRowValue(row, "image") ?? "",
      order: Number(getRowValue(row, "order") ?? 0)
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(initialForm);
    setIsFormOpen(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.push("Title is required", "error");
      return;
    }

    const customerId = isAdmin
      ? Number(form.customerId || 0)
      : Number(user?.customerId || 0);
    if (!customerId) {
      toast.push("Customer is required", "error");
      return;
    }

    const payload = {
      ...(editing ? { id: editing.id } : {}),
      customerId,
      title: form.title.trim(),
      description: form.description.trim() || null,
      image: form.image.trim() || null,
      order: Number(form.order || 0),
      inactive: false
    };

    setSaving(true);
    const res = editing
      ? await apiPut(`/api/Galleries/${editing.id}`, payload)
      : await apiPost("/api/Galleries", payload);
    setSaving(false);

    if (!res.ok) {
      toast.push(String(res.error || "Failed to save gallery"), "error");
      return;
    }

    toast.push(editing ? "Gallery updated" : "Gallery created", "success");
    closeForm();
    await load();
  };

  const removeRow = async (row) => {
    const confirmed = window.confirmDialog
      ? await window.confirmDialog("Delete this gallery item?")
      : window.confirm("Delete this gallery item?");
    if (!confirmed) return;
    setDeletingId(row.id);
    const res = await apiDelete(`/api/Galleries/${row.id}`);
    setDeletingId(0);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to delete gallery"), "error");
      return;
    }
    toast.push("Gallery deleted", "success");
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Galleries</h1>
            <p className="mt-1 text-sm text-slate-500">Manage gallery images and content in card layout.</p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Add New
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
          <input
            className={inputClass}
            placeholder="Search title/description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isAdmin && (
            <select
              className={inputClass}
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              {customers.map((c) => {
                const customerId = getRowValue(c, "id");
                return (
                  <option key={customerId} value={String(customerId)}>
                    {getRowValue(c, "name") || getRowValue(c, "code") || `Customer ${customerId}`}
                  </option>
                );
              })}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">No gallery items found.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((row) => {
            const imageSrc = resolveMediaUrl(getRowValue(row, "image"));

            return (
              <article key={row.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="relative h-44 w-full bg-slate-100">
                  {imageSrc ? (
                    <img src={imageSrc} alt={getRowValue(row, "title") || "Gallery"} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-400">No image</div>
                  )}
                </div>
                <div className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="line-clamp-1 text-base font-semibold text-slate-900">{getRowValue(row, "title") || "-"}</h2>
                    <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Order: {getRowValue(row, "order") ?? 0}</span>
                  </div>
                  <p className="line-clamp-2 text-sm text-slate-600">{getRowValue(row, "description") || "-"}</p>
                  <div className="flex items-center gap-2 pt-1">
                    <button type="button" onClick={() => openEdit(row)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void removeRow(row)}
                      disabled={deletingId === row.id}
                      className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                    >
                      {deletingId === row.id ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" role="presentation">
          <button type="button" className="absolute inset-0 bg-slate-900/50" onClick={closeForm} aria-label="Close" />
          <div className="relative z-10 w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-slate-900">{editing ? "Edit Gallery" : "Add Gallery"}</h2>
            <form onSubmit={save} className="mt-4 space-y-3">
              {isAdmin ? (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Customer *</label>
                  <select
                    className={inputClass}
                    value={form.customerId ?? ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, customerId: e.target.value }))}
                    required
                  >
                    <option value="">— Select —</option>
                    {customers.map((c) => {
                      const customerId = getRowValue(c, "id");
                      return (
                        <option key={customerId} value={String(customerId)}>
                          {getRowValue(c, "name")}{" "}
                          {getRowValue(c, "code") ? `(${getRowValue(c, "code")})` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ) : null}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Title *</label>
                <input className={inputClass} value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Description</label>
                <textarea className={`${inputClass} min-h-24`} value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
              </div>
              <ImageUploadField
                label="Image"
                value={form.image}
                onChange={(nextValue) => setForm((prev) => ({ ...prev, image: nextValue }))}
                labelClassName="mb-1.5 block text-xs font-medium text-slate-600"
              />
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Order</label>
                <input type="number" className={inputClass} value={String(form.order)} onChange={(e) => setForm((prev) => ({ ...prev, order: Number(e.target.value || 0) }))} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeForm} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60">
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
